import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, tokens, Toaster } from "@fluentui/react-components";
import { Add16Filled } from "@fluentui/react-icons";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  getFeesChargesDistributionDetailById,
  getFeesChargesDistributionListItems,
} from "../../../redux/Client/feesAndCharges";
import CaseForm from "../case/CaseForm";
import CaseList from "../case/CaseList";
import ASCButton from "../../../components/Button";
import ContentLoader from "../../../components/Loader/ContentLoader";
import ModalDialog from "../../../components/ModalDialog";
import NoDataFound from "../../../components/NoDataFound/noDataFound";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import FeesChargesTabs from "./FeesChargesTabs";

const useStyles = makeStyles({
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
});

// Distribution tab for a client Fee/Charge. Reuses the case Distribution
// form and list; the fee's commission id stands in for subSubItemId.
function FeesChargesDistribution() {
  const styles = useStyles();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { showToast, toasterId } = useToastMessage();
  const { clientId, subSubItemId, distributionId, action } = useParams();
  const [currentRecord, setCurrentRecord] = useState(1);

  const { listItems, activeItem, loading } = useSelector(
    (state: RootState) => state.feesAndCharges.distribution,
  );
  const dialogModalOptions = useSelector((state: RootState) => state.dialog);
  const basePath = `/clients/${clientId}/fees-charges/${subSubItemId}/distribution`;

  useEffect(() => {
    if (subSubItemId) dispatch(getFeesChargesDistributionListItems(subSubItemId));
  }, [subSubItemId, dispatch]);

  useEffect(() => {
    if (distributionId && listItems.length > 0) {
      dispatch(getFeesChargesDistributionDetailById(distributionId));
    }
  }, [distributionId, listItems, dispatch]);

  // Like the case Distribution tab, open on the first record.
  useEffect(() => {
    if (action !== "add" && !distributionId && listItems.length > 0) {
      navigate(
        `${basePath}/${listItems[0].commission_allocation_id}/distribution/view`,
        { replace: true },
      );
    }
  }, [action, distributionId, listItems, basePath, navigate]);

  return (
    <div>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div className={styles.header}>
        <FeesChargesTabs active="distribution" commissionId={subSubItemId} />
        {!["add", "edit"].includes(action || "") && (
          <ASCButton
            shape="rounded"
            appearance="primary"
            className="asc-button-primary"
            icon={<Add16Filled />}
            size="medium"
            onClick={() => navigate(`${basePath}/add`)}
            label="Add New"
          />
        )}
      </div>
      <div className="case_container_flex">
        {(action === "add" || listItems.length > 0) && (
          <CaseForm
            mode={action || ""}
            section="fees-charges"
            caseType="fees-charges"
            caseDetail={activeItem}
            caseItemList={listItems}
            tab="distribution"
            notify={showToast}
            currentRecord={currentRecord}
            setCurrentRecord={setCurrentRecord}
          />
        )}
        <div>
          {loading ? (
            <ContentLoader />
          ) : listItems.length > 0 ? (
            <CaseList
              module="distribution"
              data={listItems}
              activeItem={activeItem}
              section="fees-charges"
              activeTab="distribution"
              currentRecord={currentRecord}
              setCurrentRecord={setCurrentRecord}
            />
          ) : (
            action !== "add" && <NoDataFound />
          )}
        </div>
      </div>
      <ModalDialog options={dialogModalOptions} />
    </div>
  );
}

export default FeesChargesDistribution;
